import { getStroke } from "perfect-freehand";

/** One pen sample in canvas CSS pixels. */
export interface SignaturePoint {
  x: number;
  y: number;
  /** Stylus pressure 0..1, or null for a finger or mouse (the width then follows speed). */
  pressure: number | null;
}

/** One pen-down to pen-up movement. */
export type SignatureStroke = SignaturePoint[];

/** The pen width in CSS pixels before pressure or speed thins it. */
export const PEN_SIZE = 3;

/** Sheet width over height: a signature strip, not a drawing sheet. */
export const SIGNATURE_ASPECT = 3;

/** Ink colour on the record: the PDF's own text colour, whatever the screen theme. */
export const EXPORT_INK = "#1a1a1a";

// Tuned like the flashside drawing sheet: thinning by speed or pressure, rounded corners,
// damped hand jitter without a laggy pen.
const OUTLINE_OPTIONS = { thinning: 0.55, smoothing: 0.6, streamline: 0.45 };

/**
 * The stroke as a filled SVG path: perfect-freehand's outline polygon joined with quadratic
 * curves through the midpoints, so the shape has round tapered ends and no visible facets.
 * `finished` caps the tail; while the pen is down it stays open so the stroke can grow.
 */
export const strokeToPath = (stroke: SignatureStroke, finished: boolean): string => {
  if (stroke.length === 0) return "";
  const outline = getStroke(
    stroke.map((p) => [p.x, p.y, p.pressure ?? 0.5]),
    {
      ...OUTLINE_OPTIONS,
      size: PEN_SIZE,
      simulatePressure: stroke.every((p) => p.pressure === null),
      last: finished,
    }
  );
  if (outline.length === 0) return "";
  const [first, ...rest] = outline;
  if (rest.length === 0) return `M ${round(first[0])} ${round(first[1])} Z`;
  const parts = [`M ${round(first[0])} ${round(first[1])}`];
  let previous = first;
  for (const point of rest) {
    parts.push(
      `Q ${round(previous[0])} ${round(previous[1])} ${round((previous[0] + point[0]) / 2)} ${round(
        (previous[1] + point[1]) / 2
      )}`
    );
    previous = point;
  }
  parts.push(
    `Q ${round(previous[0])} ${round(previous[1])} ${round((previous[0] + first[0]) / 2)} ${round(
      (previous[1] + first[1]) / 2
    )}`,
    "Z"
  );
  return parts.join(" ");
};

const round = (value: number): number => Math.round(value * 100) / 100;

/** Whether there is anything to sign with: a tap counts, as a dot does on paper. */
export const hasInk = (strokes: SignatureStroke[]): boolean => strokes.some((s) => s.length > 0);

export interface InkBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The box around the ink, padded, clamped to the sheet. The export crops to it so the
 * record prints the signature itself, not a mostly empty strip.
 */
export const inkBounds = (
  strokes: SignatureStroke[],
  sheetWidth: number,
  sheetHeight: number,
  padding: number = PEN_SIZE * 2
): InkBounds | null => {
  const points = strokes.flat();
  if (points.length === 0) return null;
  const minX = Math.max(0, Math.min(...points.map((p) => p.x)) - padding);
  const minY = Math.max(0, Math.min(...points.map((p) => p.y)) - padding);
  const maxX = Math.min(sheetWidth, Math.max(...points.map((p) => p.x)) + padding);
  const maxY = Math.min(sheetHeight, Math.max(...points.map((p) => p.y)) + padding);
  return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
};

/** Paints the strokes in `ink`, `scale` device pixels per CSS pixel, shifted by `-offset`. */
export const paintStrokes = (
  context: CanvasRenderingContext2D,
  strokes: SignatureStroke[],
  live: SignatureStroke | null,
  ink: string,
  scale: number,
  offset: { x: number; y: number } = { x: 0, y: 0 }
): void => {
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  context.setTransform(scale, 0, 0, scale, -offset.x * scale, -offset.y * scale);
  context.fillStyle = ink;
  for (const stroke of strokes) context.fill(new Path2D(strokeToPath(stroke, true)));
  if (live) context.fill(new Path2D(strokeToPath(live, false)));
};

/** Pixels per CSS pixel in the exported PNG: crisp on the record, bounded so the upload stays small. */
export const exportScale = (devicePixelRatio: number): number =>
  Math.min(3, Math.max(2, Number.isFinite(devicePixelRatio) ? devicePixelRatio : 1));

/**
 * The signature as a transparent PNG data URL, cropped to the ink and drawn in the record's
 * ink colour; null when the browser cannot encode it.
 */
export const exportSignaturePng = (
  strokes: SignatureStroke[],
  sheetWidth: number,
  sheetHeight: number,
  devicePixelRatio: number
): string | null => {
  const bounds = inkBounds(strokes, sheetWidth, sheetHeight);
  if (!bounds) return null;
  const scale = exportScale(devicePixelRatio);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bounds.width * scale);
  canvas.height = Math.round(bounds.height * scale);
  const context = canvas.getContext("2d");
  if (!context) return null;
  paintStrokes(context, strokes, null, EXPORT_INK, scale, { x: bounds.x, y: bounds.y });
  const dataUrl = canvas.toDataURL("image/png");
  return dataUrl.startsWith("data:image/png") ? dataUrl : null;
};

/** The base64 payload of a PNG data URL, the shape the API takes. */
export const dataUrlToBase64 = (dataUrl: string): string => dataUrl.slice(dataUrl.indexOf(",") + 1);
